import React from 'react';

interface ProfileImage {
  size: 'small' | 'medium' | 'large' | 'xlarge';
  opacity: number;
  position: {
    top: string;
    right: string;
  };
  imageSrc: string;
  alt: string;
  showOnMobile?: boolean;
}

export interface GrowthPlaybooksBannerProps {
  activeFilter?: string | null;
  onFilterChange?: (nextStage: string | null) => void;
}

const GrowthPlaybooksBanner: React.FC<GrowthPlaybooksBannerProps> = () => {
  const profileImages: ProfileImage[] = [
    {
      size: 'small',
      opacity: 0.5,
      position: { top: '0px', right: '401px' },
      imageSrc: '/images/profiles/image 442.png',
      alt: 'Founder profile',
      showOnMobile: false,
    },
    {
      size: 'medium',
      opacity: 0.5,
      position: { top: '-13px', right: '88px' },
      imageSrc: '/images/profiles/image 449.png',
      alt: 'Founder profile',
      showOnMobile: false,
    },
    {
      size: 'xlarge',
      opacity: 1,
      position: { top: '24px', right: '364px' },
      imageSrc: '/images/profiles/image 445.png',
      alt: 'Founder profile',
      showOnMobile: true,
    },
    {
      size: 'medium',
      opacity: 0.5,
      position: { top: '164px', right: '421px' },
      imageSrc: '/images/profiles/image 443.png',
      alt: 'Founder profile',
      showOnMobile: false,
    },
    {
      size: 'large',
      opacity: 1,
      position: { top: '20px', right: '190px' },
      imageSrc: '/images/profiles/image 446.png',
      alt: 'Founder profile',
      showOnMobile: true,
    },
    {
      size: 'small',
      opacity: 0.2,
      position: { top: '203px', right: '42px' },
      imageSrc: '/images/profiles/image 448.png',
      alt: 'Founder profile',
      showOnMobile: false,
    },
    {
      size: 'large',
      opacity: 1,
      position: { top: '184px', right: '317px' },
      imageSrc: '/images/profiles/image 450.png',
      alt: 'Founder profile',
      showOnMobile: false,
    },
    {
      size: 'small',
      opacity: 0.2,
      position: { top: '106px', right: '4px' },
      imageSrc: '/images/profiles/image 447.png',
      alt: 'Founder profile',
      showOnMobile: false,
    },
    {
      size: 'xlarge',
      opacity: 1,
      position: { top: '139px', right: '60px' },
      imageSrc: '/images/profiles/image 426.png',
      alt: 'Founder profile',
      showOnMobile: true,
    },
  ];

  const getSizeClasses = (size: ProfileImage['size']) => {
    switch (size) {
      case 'small':
        return 'w-6 h-6 md:w-8 md:h-8';
      case 'medium':
        return 'w-10 h-10 md:w-14 md:h-14';
      case 'large':
        return 'w-14 h-14 md:w-20 md:h-20';
      case 'xlarge':
        return 'w-20 h-20 md:w-36 md:h-36';
      default:
        return 'w-8 h-8 md:w-12 md:h-12';
    }
  };

  return (
    <div className="w-full max-w-7xl mx-auto mb-8 min-w-0">
      <div className="relative bg-[#F0F6FF] dark:bg-zinc-900 border border-[#3B6CF2] dark:border-zinc-700 dark:shadow-[0_0_0_1px_rgba(59,108,242,0.12)_inset,0_1px_0_0_rgba(255,255,255,0.04)_inset] rounded-[20px] overflow-hidden">
        <div className="md:hidden">
          <div className="p-4 pb-3 sm:p-6 sm:pb-4">
            <div className="text-[#81A2FF] dark:text-blue-400 font-bold text-xs leading-[15px] mb-3">
              Growth Playbooks
            </div>

            <h1 className="text-[#2C5EEA] dark:text-blue-300 font-bold text-lg leading-snug mb-4 text-balance">
              100 Founders, 1 Growth Platform
            </h1>

            <div className="text-black dark:text-zinc-200 font-normal text-sm leading-relaxed space-y-3">
              <p>
                We reverse-engineered the exact strategies behind the growth of Apollo, Notion, Clay and many
                more. Then we turned them into simple playbooks, ready to use.
              </p>

              <p className="font-bold">
                Even if you have no audience, no budget, and no idea where to start.
              </p>
            </div>
          </div>

          <div className="px-4 pb-5 sm:px-6 sm:pb-6">
            <div className="flex flex-col items-center gap-3 border-t border-[#3B6CF2]/20 pt-4 dark:border-zinc-700">
              <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
                {profileImages
                  .filter((profile) => profile.showOnMobile)
                  .slice(0, 3)
                  .map((profile, index) => (
                    <div
                      key={profile.imageSrc}
                      className="h-12 w-12 shrink-0 overflow-hidden rounded-full border-2 border-white shadow-md dark:border-zinc-700"
                      style={{ opacity: profile.opacity }}
                    >
                      <img
                        src={profile.imageSrc}
                        alt={profile.alt}
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            parent.className +=
                              ' bg-gradient-to-br from-blue-400 to-sky-500 flex items-center justify-center text-white font-semibold';
                            parent.innerHTML = `<div class="text-xs font-bold">${String.fromCharCode(65 + index)}</div>`;
                          }
                        }}
                      />
                    </div>
                  ))}
              </div>
              <div className="text-center text-[11px] font-medium leading-tight text-[#2C5EEA] dark:text-blue-300 sm:text-xs">
                +97 more founders
              </div>
            </div>
          </div>
        </div>

        <div className="hidden md:block relative h-[236px]">
          <div className="absolute left-[42px] top-[24px] right-auto max-w-[503px] z-10">
            <div className="text-[#81A2FF] dark:text-blue-400 font-bold text-xs leading-[15px] mb-2">
              Growth Playbooks
            </div>

            <h1 className="text-[#2C5EEA] dark:text-blue-300 font-bold text-2xl lg:text-[28px] leading-tight md:leading-[34px] mb-3 md:mb-4">
              100 Founders, 1 Growth Platform
            </h1>

            <div className="text-black dark:text-zinc-200 font-normal text-[15px] leading-6">
              <p className="mb-2">
                We reverse-engineered the exact strategies behind the growth of Apollo, Notion, Clay and many
                more. Then we turned them into simple playbooks, ready to use.
              </p>

              <p className="font-bold">
                Even if you have no audience, no budget, and no idea where to start.
              </p>
            </div>
          </div>

          <div className="absolute right-0 top-0 w-[400px] h-full">
            {profileImages.map((profile, index) => (
              <div
                key={index}
                className={`absolute ${getSizeClasses(profile.size)} rounded-full overflow-hidden border-2 border-white dark:border-zinc-700 shadow-lg`}
                style={{
                  top: profile.position.top,
                  right: profile.position.right,
                  opacity: profile.opacity,
                }}
              >
                <img
                  src={profile.imageSrc}
                  alt={profile.alt}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent) {
                      parent.className += ' bg-gradient-to-br from-blue-400 to-sky-500 flex items-center justify-center text-white font-semibold';
                      parent.innerHTML = `<div class="text-xs font-bold">${String.fromCharCode(65 + index)}</div>`;
                    }
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GrowthPlaybooksBanner;
